import * as React from 'react';
import { Button, Stack, Tooltip, Typography } from '@mui/material';
import { useFormikContext } from 'formik';
import { Discounts } from '@prisma/client';
import {
  CheckoutFormValues,
  CheckoutPaymentMethod,
  SelectedAppointment,
} from '../../types/appointments';
import { isAppointmentDepositPaid } from '../../helpers/appointments';
import PaymentMethodsList from '../checkout/PaymentMethodsList';
import { DEFAULT_TOOLTIP_PROPS } from '../../constants/ui';
import type useGetCheckoutData from '../../hooks/checkouts/useGetCheckoutData';
import {
  isFreeWithDiscount,
  isGuestCheckout as checkIsGuestCheckout,
} from '../../helpers/payments';
import { GiftCardType } from '../../types/payments';

interface Props {
  checkoutData: ReturnType<typeof useGetCheckoutData>;
  enabledPaymentMethods: CheckoutPaymentMethod[];
  selectedAppointment?: SelectedAppointment | null;
  selectedDiscount?: Discounts | null;
  onSplitPayment: () => void;
}

export default function PaymentMethod({
  enabledPaymentMethods,
  selectedAppointment,
  selectedDiscount,
  onSplitPayment,
  checkoutData,
}: Props) {
  const { values, setFieldValue } = useFormikContext<CheckoutFormValues>();

  const total = checkoutData.data?.totalPriceWithoutGiftCardCents ?? 0;
  const isGuestCheckout = checkIsGuestCheckout({
    customer: values.customer,
    appointment: selectedAppointment,
  });
  const splitRequiresCustomer =
    !isGuestCheckout && !values.customer?.phoneNumber;

  const isDepositPaid = React.useMemo(
    () => isAppointmentDepositPaid(selectedAppointment),
    [selectedAppointment],
  );

  return (
    <div className="input-group">
      <Stack
        direction="row"
        justifyContent="space-between"
        alignItems="end"
        width="100%">
        <Typography variant="h2">Payment Method</Typography>
        <Tooltip
          {...DEFAULT_TOOLTIP_PROPS}
          title={
            splitRequiresCustomer
              ? 'Please select or create a customer to split payment'
              : undefined
          }>
          <span>
            <Button
              variant="light"
              color="info"
              onClick={() => onSplitPayment()}
              disabled={splitRequiresCustomer || total <= 0}>
              Split Payment
            </Button>
          </span>
        </Tooltip>
      </Stack>
      <PaymentMethodsList
        totalPriceCents={total}
        giftCardAmountCents={total}
        paymentMethods={enabledPaymentMethods}
        selectedPaymentMethod={values.paymentMethod}
        hasAppointment={!!selectedAppointment}
        onChange={async value => {
          if (
            value === 'FREE' &&
            selectedAppointment &&
            !isFreeWithDiscount(value, selectedDiscount)
          ) {
            if (values.additionalServices?.length) {
              setFieldValue('additionalServices', []);
            }
          }

          setFieldValue('paymentMethod', value);
          if (value === 'FREE') {
            if (!isFreeWithDiscount(value, selectedDiscount)) {
              const newPrice = isDepositPaid
                ? selectedAppointment?.depositPrice
                : 0;
              setFieldValue('price', newPrice || 0);
            }
            setFieldValue('tipAmt', 0);
            setFieldValue('tipPercentage', null);
            // set it back to original price if previous is FREE
          } else if (values.paymentMethod === 'FREE') {
            setFieldValue('price', selectedAppointment?.price ?? 0);
          }
        }}
        customer={values.customer}
        giftCardType={{
          value: values.giftCardType,
          set: (value: GiftCardType) => {
            setFieldValue('giftCardType', value);
          },
        }}
      />
    </div>
  );
}
